import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import { Modal } from '../../../../shared/ui/Modal';
import { TextInput } from '../../../../shared/ui/TextInput';
import {
  Footer,
  FormBody,
  FormWrapper,
  Grid,
  Header,
  Section,
  SectionLabel,
  SectionTitle,
} from '../../../../shared/ui/Modal/Body';
import {
  Question,
  QuestionOptionPayload,
  QuestionPayload,
} from '../../../../datasources/questions/QuestionsDataSource';
import { Basket } from '../../../../types/basket/Basket';
import { v1 as uuidv1 } from 'uuid';

type QuestionOptionForm = {
  id?: number;
  question_id?: number;
  sort_order?: number;
  basket_id?: number | null;
  action: 'SHOW_BASKET' | null;
  value: string;
  label: string;
};

type QuestionForm = {
  text: string;
  type: 'SELECT' | 'BOOLEAN' | 'TEXT';
  required: boolean;
  is_loop: boolean;
};

type OptionValidationErrors = {
  label?: string;
  basket?: string;
};

type QuestionValidationErrors = {
  text?: string;
  options?: string;
  optionFields: OptionValidationErrors[];
};

type DialogQuestionProps = {
  open: boolean;
  mode: 'create' | 'edit';
  onClose: () => void;
  loading?: boolean;
  error?: string;
  onConfirm: (payload: QuestionPayload) => void | Promise<void>;
  questions: Question[];
  baskets: Basket[];
  basketsLoading: boolean;
  basketsError?: string;
  initialQuestion?: Question;
};

const initialForm: QuestionForm = {
  text: '',
  type: 'SELECT',
  required: true,
  is_loop: false,
};

function createOption(value: string, label = ''): QuestionOptionForm {
  return {
    value,
    label,
    basket_id: null,
    action: null,
  };
}

export const DialogQuestion: React.FC<DialogQuestionProps> = ({
  open,
  mode,
  onClose,
  loading = false,
  error,
  onConfirm,
  questions,
  baskets,
  basketsLoading,
  basketsError,
  initialQuestion,
}) => {
  const [form, setForm] = useState<QuestionForm>(initialForm);
  const [options, setOptions] = useState<QuestionOptionForm[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const gridCols = useMediaQuery('(max-width: 1224px)') ? 1 : 2;

  function generateUniqueValue(additionalValues: string[] = []): string {
    const existingValues = new Set([
      ...questions.flatMap((question) =>
        question.options.map((option) => option.value)
      ),
      ...options.map((option) => option.value),
      ...additionalValues,
    ]);
    let value = uuidv1();
    while (existingValues.has(value)) {
      value = uuidv1();
    }
    return value;
  }

  function createBooleanOptions(): QuestionOptionForm[] {
    const trueValue = generateUniqueValue();
    const falseValue = generateUniqueValue([trueValue]);
    return [
      createOption(trueValue, 'Verdadeiro'),
      createOption(falseValue, 'Falso'),
    ];
  }

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm({
      text: initialQuestion?.text ?? '',
      type:
        initialQuestion?.type === 'BOOLEAN' || initialQuestion?.type === 'TEXT'
          ? initialQuestion.type
          : 'SELECT',
      required: initialQuestion?.required ?? true,
      is_loop: initialQuestion?.is_loop ? true : false,
    });
    const initialOptions =
      initialQuestion?.options
        ?.slice()
        .sort((first, second) => first.sort_order - second.sort_order)
        .map((option) => ({
          id: option.id,
          question_id: option.question_id,
          sort_order: option.sort_order,
          basket_id: option.basket_id,
          action:
            option.action ?? (option.basket_id != null ? 'SHOW_BASKET' : null),
          value: option.value,
          label: option.label,
        })) ?? [];
    if (initialQuestion?.type === 'BOOLEAN') {
      const booleanOptions: QuestionOptionForm[] = initialOptions
        .slice(0, 2)
        .map((option, index) => ({
          ...option,
          label: option.label || (index === 0 ? 'Verdadeiro' : 'Falso'),
        }));
      while (booleanOptions.length < 2) {
        booleanOptions.push(
          createOption(
            generateUniqueValue(booleanOptions.map((option) => option.value)),
            booleanOptions.length === 0 ? 'Verdadeiro' : 'Falso'
          )
        );
      }
      setOptions(booleanOptions);
    } else {
      setOptions(
        initialQuestion?.type === 'TEXT'
          ? initialOptions
          : initialOptions.length > 0
          ? initialOptions
          : [createOption(generateUniqueValue())]
      );
    }
    setHasSubmitted(false);
  }, [open, initialQuestion]);

  function validateForm(): QuestionValidationErrors {
    const errors: QuestionValidationErrors = {
      optionFields: options.map(() => ({})),
    };
    if (!form.text.trim()) {
      errors.text = 'Informe o texto da pergunta.';
    } else if (form.text.trim().length > 500) {
      errors.text = 'O texto deve possuir no máximo 500 caracteres.';
    }

    if (form.type === 'SELECT') {
      if (options.length === 0) {
        errors.options = 'Adicione pelo menos uma opção de resposta.';
      }

      options.forEach((option, index) => {
        if (!option.label.trim()) {
          errors.optionFields[index].label = 'Informe o rótulo desta opção.';
        }
      });
    } else if (form.type === 'BOOLEAN' && options.length !== 2) {
      errors.options = 'A pergunta deve possuir as opções Verdadeiro e Falso.';
    }

    options.forEach((option, index) => {
      if (form.type !== 'TEXT' && option.action === 'SHOW_BASKET') {
        if (option.basket_id == null) {
          errors.optionFields[index].basket =
            'Selecione um balaio para esta resposta.';
        } else if (!baskets.some((basket) => basket.id === option.basket_id)) {
          errors.optionFields[index].basket =
            'O balaio associado não está mais disponível.';
        }
      }
    });

    return errors;
  }

  const validationErrors = validateForm();
  const formValid = Boolean(
    !validationErrors.text &&
      !validationErrors.options &&
      !validationErrors.optionFields.some(
        (optionError) => optionError.label || optionError.basket
      )
  );
  const confirmDisabled = loading || basketsLoading || Boolean(basketsError);

  function updateOption(
    index: number,
    updates: Partial<QuestionOptionForm>
  ): void {
    setOptions((currentOptions) =>
      currentOptions.map((option, optionIndex) =>
        optionIndex === index ? { ...option, ...updates } : option
      )
    );
  }

  function buildOptions(): QuestionOptionPayload[] {
    if (form.type !== 'SELECT') {
      if (form.type === 'TEXT' && initialQuestion?.type === 'TEXT') {
        return initialQuestion.options.map((option, index) => ({
          ...(option.id !== undefined ? { id: option.id } : {}),
          ...(option.question_id !== undefined
            ? { question_id: option.question_id }
            : {}),
          value: option.value,
          label: option.label,
          sort_order: index + 1,
          basket_id: option.basket_id ?? null,
          action: option.action,
        }));
      }
      return options.map((option, index) => ({
        ...(option.id !== undefined ? { id: option.id } : {}),
        ...(option.question_id !== undefined
          ? { question_id: option.question_id }
          : {}),
        value: option.value,
        label: option.label,
        sort_order: index + 1,
        basket_id:
          option.action === 'SHOW_BASKET' ? option.basket_id ?? null : null,
        action: option.action,
      }));
    }

    return options.map((option, index) => ({
      ...(option.id !== undefined ? { id: option.id } : {}),
      ...(option.question_id !== undefined
        ? { question_id: option.question_id }
        : {}),
      value: option.value.trim(),
      label: option.label.trim(),
      sort_order: index + 1,
      basket_id:
        option.action === 'SHOW_BASKET' ? option.basket_id ?? null : null,
      action: option.action,
    }));
  }

  function handleTypeChange(nextType: QuestionForm['type']): void {
    if (nextType === form.type) {
      return;
    }

    const hasOptionData = options.some(
      (option) =>
        option.id !== undefined ||
        option.label.trim() ||
        option.action !== null ||
        option.basket_id != null
    );
    if (
      hasOptionData &&
      !window.confirm(
        'Alterar o tipo substituirá as opções e os balaios associados. Deseja continuar?'
      )
    ) {
      return;
    }

    setForm((currentForm) => ({ ...currentForm, type: nextType }));
    if (nextType === 'BOOLEAN') {
      setOptions(createBooleanOptions());
    } else if (nextType === 'SELECT') {
      setOptions([createOption(generateUniqueValue())]);
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!formValid) {
      setHasSubmitted(true);
      return;
    }

    await onConfirm({
      text: form.text.trim(),
      type: form.type,
      required: form.required,
      is_loop: form.is_loop,
      options: buildOptions(),
    });
  }

  return (
    <Modal open={open} width="min(720px, 95vw)" gap={10}>
      <Header>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#444' }}>
          {mode === 'edit' ? 'Editar Pergunta' : 'Nova Pergunta'}
        </span>
      </Header>
      <FormBody>
        <SectionTitle>Dados da Pergunta</SectionTitle>
        <FormWrapper>
          <Section>
            <SectionLabel>Identificação</SectionLabel>
            <Grid cols={gridCols}>
              <TextField
                select
                size="small"
                label="Tipo de pergunta"
                value={form.type}
                onChange={(event) =>
                  handleTypeChange(event.target.value as QuestionForm['type'])
                }
                disabled={loading}
              >
                <MenuItem value="SELECT">Escolha</MenuItem>
                <MenuItem value="BOOLEAN">Verdadeiro ou falso</MenuItem>
                {mode === 'edit' &&
                  initialQuestion?.type === 'TEXT' &&
                  form.type === 'TEXT' && (
                    <MenuItem value="TEXT">Texto livre (legada)</MenuItem>
                  )}
              </TextField>
            </Grid>
            <TextInput
              value={form.text}
              label="Texto da pergunta"
              handleChange={(event) => {
                setForm((currentForm) => ({
                  ...currentForm,
                  text: event.target.value,
                }));
              }}
              disabled={loading}
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              error={hasSubmitted && Boolean(validationErrors.text)}
              helperText={hasSubmitted ? validationErrors.text : undefined}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.required}
                    onChange={(event) =>
                      setForm((currentForm) => ({
                        ...currentForm,
                        required: event.target.checked,
                      }))
                    }
                    disabled={loading}
                  />
                }
                label="Pergunta obrigatória"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_loop}
                    onChange={(event) =>
                      setForm((currentForm) => ({
                        ...currentForm,
                        is_loop: event.target.checked,
                      }))
                    }
                    disabled={loading}
                  />
                }
                label="Permitir repetição?"
              />
            </div>
          </Section>

          {form.type !== 'TEXT' && (
            <Section style={{ gap: 14 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div>
                  <SectionLabel style={{ margin: 0 }}>
                    Opções de resposta
                  </SectionLabel>
                </div>
                {form.type === 'SELECT' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={loading}
                    onClick={() => {
                      setOptions((currentOptions) => [
                        ...currentOptions,
                        createOption(
                          generateUniqueValue(
                            currentOptions.map((option) => option.value)
                          )
                        ),
                      ]);
                    }}
                  >
                    Adicionar opção
                  </Button>
                )}
              </div>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {options.map((option, index) => {
                  const isBoolean = form.type === 'BOOLEAN';
                  const isBasketAction = option.action === 'SHOW_BASKET';
                  const decisionLabel = index === 0 ? 'Verdadeiro' : 'Falso';

                  return (
                    <Paper
                      key={option.id ?? `new-option-${index}`}
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        borderColor: isBasketAction
                          ? 'primary.light'
                          : 'divider',
                        bgcolor: isBasketAction
                          ? 'rgba(25, 118, 210, 0.025)'
                          : 'background.paper',
                        transition:
                          'border-color 160ms ease, background-color 160ms ease',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          aria-hidden="true"
                          sx={{
                            flex: '0 0 32px',
                            height: 32,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: '50%',
                            bgcolor: isBoolean
                              ? index === 0
                                ? 'success.light'
                                : 'warning.light'
                              : 'action.hover',
                            color: isBoolean
                              ? index === 0
                                ? 'success.dark'
                                : 'warning.dark'
                              : 'text.secondary',
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          {isBoolean ? (index === 0 ? 'V' : 'F') : index + 1}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {isBoolean ? (
                                <>
                                  <Typography variant="subtitle2">
                                    {decisionLabel}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Decisão da pergunta
                                  </Typography>
                                </>
                              ) : (
                                <TextInput
                                  value={option.label}
                                  label="Rótulo da resposta"
                                  handleChange={(event) =>
                                    updateOption(index, {
                                      label: event.target.value,
                                    })
                                  }
                                  disabled={loading}
                                  error={
                                    hasSubmitted &&
                                    Boolean(
                                      validationErrors.optionFields[index]
                                        ?.label
                                    )
                                  }
                                  helperText={
                                    hasSubmitted
                                      ? validationErrors.optionFields[index]
                                          ?.label
                                      : undefined
                                  }
                                />
                              )}
                            </Box>
                            {!isBoolean && options.length > 1 && (
                              <Tooltip title="Remover resposta">
                                <span>
                                  <IconButton
                                    color="error"
                                    aria-label={`Remover opção ${index + 1}`}
                                    disabled={loading}
                                    onClick={() => {
                                      setOptions((currentOptions) =>
                                        currentOptions.filter(
                                          (_, optionIndex) =>
                                            optionIndex !== index
                                        )
                                      );
                                    }}
                                    sx={{ mt: 0.25 }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>

                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.25,
                              borderRadius: 1.5,
                              bgcolor: 'action.hover',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                              }}
                            >
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={isBasketAction}
                                    onChange={(event) => {
                                      updateOption(index, {
                                        action: event.target.checked
                                          ? 'SHOW_BASKET'
                                          : null,
                                        basket_id: event.target.checked
                                          ? option.basket_id ?? null
                                          : null,
                                      });
                                    }}
                                    disabled={loading}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontWeight={600}>
                                    Exibir balaio nesta resposta
                                  </Typography>
                                }
                              />
                              <ShoppingBasketOutlinedIcon
                                fontSize="small"
                                color={isBasketAction ? 'primary' : 'disabled'}
                                aria-hidden="true"
                              />
                            </Box>

                            {isBasketAction && (
                              <Box sx={{ mt: 1, pl: { xs: 0, sm: 4.5 } }}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Balaio associado"
                                  value={option.basket_id ?? ''}
                                  onChange={(event) => {
                                    const basketId = event.target.value
                                      ? Number(event.target.value)
                                      : null;
                                    updateOption(index, {
                                      basket_id: basketId,
                                    });
                                  }}
                                  disabled={
                                    loading ||
                                    basketsLoading ||
                                    Boolean(basketsError)
                                  }
                                  error={
                                    hasSubmitted &&
                                    Boolean(
                                      validationErrors.optionFields[index]
                                        ?.basket
                                    )
                                  }
                                  helperText={
                                    hasSubmitted
                                      ? validationErrors.optionFields[index]
                                          ?.basket
                                      : undefined
                                  }
                                >
                                  <MenuItem value="">
                                    Selecione um balaio
                                  </MenuItem>
                                  {baskets.map((basket) => (
                                    <MenuItem key={basket.id} value={basket.id}>
                                      {basket.code} - {basket.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Section>
          )}

          {loading && <LinearProgress />}
          {hasSubmitted && validationErrors.options && (
            <Alert severity="error">{validationErrors.options}</Alert>
          )}
          {basketsError && <Alert severity="error">{basketsError}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </FormWrapper>
      </FormBody>
      <Footer>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmDisabled}
        >
          {loading ? 'Salvando...' : 'Confirmar'}
        </Button>
      </Footer>
    </Modal>
  );
};
